import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

// CSS deliberately mirrors Employees.js's class names/look 1:1 (same
// .adm-* classes) so the two HRMS pages feel like one module, even though
// each page carries its own copy of the block (Employees.js is already
// 1200+ lines — kept these pages as separate files rather than merging).
const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg: #f0f4fc; --surface: #ffffff; --surface2: #f4f8ff;
  --border: #c0d0eb; --border2: #d5e3f5;
  --accent: #0284c7; --text: #0d1a36; --text2: #2a4a78; --muted: #6080aa;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
body { background: var(--bg); color: var(--text); }
.adm-topbar { position: sticky; top: 0; z-index: 100; height: 52px; display: flex; align-items: center; background: rgba(240,244,252,.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border2); }
.adm-back { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; background: none; border: none; border-right: 1px solid var(--border2); color: var(--text2); cursor: pointer; font-size: 16px; flex-shrink: 0; transition: color .15s, background .15s; }
.adm-back:hover { color: var(--text); background: var(--surface); }
.adm-brand { display: flex; align-items: center; gap: 10px; padding: 0 20px; flex: 1; }
.adm-brand-mark { width: 26px; height: 26px; border-radius: 6px; background: linear-gradient(135deg, #0284c7, #2563eb); display: flex; align-items: center; justify-content: center; font-size: 13px; }
.adm-brand-label { font-size: 14px; font-weight: 600; color: var(--text); }
.adm-avatar { margin-right: 16px; width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #7c3aed); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
.adm-tabs { display: flex; align-items: center; gap: 2px; padding: 0 20px; border-bottom: 1px solid var(--border2); background: var(--surface); overflow-x: auto; }
.adm-tab { padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--muted); background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color .15s; font-family: inherit; white-space: nowrap; }
.adm-tab:hover { color: var(--text2); }
.adm-tab.active { color: var(--accent); font-weight: 700; border-bottom-color: var(--accent); }
.adm-main { max-width: 1200px; margin: 0 auto; padding: 28px 24px 80px; }
.adm-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 24px; margin-bottom: 14px; box-shadow: 0 1px 2px rgba(13,26,54,.04), 0 4px 12px rgba(13,26,54,.05); }
.adm-card-title { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between; }
.adm-table-wrap { overflow-x: auto; }
.adm-table { width: 100%; border-collapse: collapse; min-width: 700px; }
.adm-table th { font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--muted); padding: 0 12px 10px; text-align: left; border-bottom: 1px solid var(--border2); white-space: nowrap; }
.adm-table td { padding: 8px 10px; font-size: 13px; color: var(--text2); border-bottom: 1px solid var(--border2); vertical-align: middle; }
.adm-table tr:last-child td { border-bottom: none; }
.adm-table tr:hover td { background: var(--surface2); }
.adm-table tr.clickable { cursor: pointer; }
.adm-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 20px; font-size: 10px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; border: 1px solid; white-space: nowrap; }
.adm-badge-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.adm-action-btn { background: none; border: 1px solid var(--border); border-radius: 6px; padding: 4px 9px; font-size: 11px; color: var(--text2); cursor: pointer; font-family: inherit; transition: border-color .15s, color .15s; margin-right: 4px; white-space: nowrap; }
.adm-action-btn:hover { border-color: var(--accent); color: var(--accent); }
.adm-back-link { display: inline-flex; align-items: center; gap: 6px; background: var(--surface2); border: 1px solid var(--border2); border-radius: 8px; padding: 8px 14px; font-size: 12px; font-weight: 600; color: var(--text2); cursor: pointer; font-family: inherit; transition: border-color .15s, color .15s, background .15s; white-space: nowrap; }
.adm-back-link:hover { border-color: var(--accent); color: var(--accent); background: rgba(2,132,199,.06); }
.adm-action-btn:disabled { opacity: .5; cursor: not-allowed; }
.adm-form-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
@media(max-width:700px){ .adm-form-grid{ grid-template-columns:1fr; } }
.adm-field { display: flex; flex-direction: column; gap: 6px; }
.adm-label { font-size: 11px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: var(--muted); }
.adm-input { background: var(--surface2); border: 1px solid var(--border2); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); outline: none; font-family: inherit; transition: border-color .15s; width: 100%; }
.adm-input:focus { border-color: var(--accent); }
.adm-input:disabled { opacity: .5; cursor: not-allowed; background: var(--border2); }
.adm-input-sm { padding: 6px 8px; font-size: 12px; width: 90px; }
.adm-select { background: var(--surface2); border: 1px solid var(--border2); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); outline: none; font-family: inherit; cursor: pointer; }
.adm-submit { padding: 10px 24px; border-radius: 7px; background: linear-gradient(135deg, #0284c7, #2563eb); border: none; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; transition: opacity .15s, box-shadow .15s; box-shadow: 0 2px 8px rgba(2,132,199,.25); }
.adm-submit:hover { opacity: .9; box-shadow: 0 4px 14px rgba(2,132,199,.35); }
.adm-submit:disabled { opacity: .5; cursor: not-allowed; }
/* Same footprint as .adm-submit, outlined instead of filled — for a
   secondary action sitting next to (or replacing the emphasis of) a
   primary one, e.g. "Generate Another Version" once a file already exists. */
.adm-btn-secondary { padding: 10px 24px; border-radius: 7px; background: none; border: 1.5px solid var(--border); color: var(--text2); font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; transition: border-color .15s, color .15s, background .15s; white-space: nowrap; }
.adm-btn-secondary:hover { border-color: var(--accent); color: var(--accent); background: rgba(2,132,199,.06); }
.adm-btn-secondary:disabled { opacity: .5; cursor: not-allowed; }
/* The "a file already exists for this period" banner — deliberately the
   loudest thing in the WPS card, so it can't be missed by scrolling past
   the generate form without noticing a file is already sitting there. */
.adm-wps-existing { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; background: rgba(16,185,129,.08); border: 1px solid rgba(16,185,129,.25); border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; }
.adm-wps-existing-text { font-size: 13px; color: #059669; }
.adm-section-btn { background: none; border: 1px solid var(--accent); border-radius: 7px; padding: 7px 14px; font-size: 12px; color: var(--accent); cursor: pointer; font-family: inherit; font-weight: 600; transition: background .15s; }
.adm-section-btn:hover { background: rgba(2,132,199,.1); }
.adm-section-btn:disabled { opacity: .5; cursor: not-allowed; }
.adm-error { padding: 10px 14px; border-radius: 7px; background: rgba(248,113,113,.08); border: 1px solid rgba(248,113,113,.2); color: #dc2626; font-size: 13px; margin-bottom: 14px; }
.adm-toast { position: fixed; top: 68px; right: 20px; z-index: 300; padding: 12px 16px; border-radius: 8px;
  background: #ffffff; border: 1px solid rgba(16,185,129,.3); color: #059669; font-size: 13px; font-weight: 500;
  box-shadow: 0 4px 16px rgba(13,26,54,.12), 0 1px 3px rgba(13,26,54,.08); max-width: 360px;
  display: flex; align-items: center; gap: 8px; animation: admToastIn .25s ease-out; }
@keyframes admToastIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
.adm-toast-dot { width: 8px; height: 8px; border-radius: 50%; background: #059669; flex-shrink: 0; }
.adm-num { text-align: right; font-variant-numeric: tabular-nums; }
.adm-warning { padding: 12px 14px; border-radius: 7px; background: rgba(245,158,11,.1); border: 1px solid rgba(245,158,11,.3); color: #b45309; font-size: 13px; margin-bottom: 14px; }
.adm-spinner-wrap { display: flex; justify-content: center; align-items: center; height: 200px; }
.adm-spinner { width: 32px; height: 32px; border: 3px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: aspin .6s linear infinite; }
@keyframes aspin { to { transform: rotate(360deg); } }
.adm-empty { font-size: 13px; color: var(--muted); padding: 28px 20px; text-align: center; border: 1px dashed var(--border2); border-radius: 8px; background: var(--surface2); }
.adm-summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
@media(max-width:700px){ .adm-summary-grid{ grid-template-columns:1fr 1fr; } }
.adm-summary-stat { background: var(--surface2); border: 1px solid var(--border2); border-radius: 8px; padding: 12px 14px; }
.adm-summary-stat .v { font-size: 18px; font-weight: 700; color: var(--text); }
.adm-summary-stat .l { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; margin-top: 2px; }
.adm-card-desc { font-size: 12px; color: var(--muted); margin: -8px 0 14px; line-height: 1.5; }
.adm-toggle { position: relative; display: inline-block; width: 38px; height: 22px; flex-shrink: 0; }
.adm-toggle input { position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; z-index: 1; }
.adm-toggle input:disabled { cursor: not-allowed; }
.adm-toggle-track { position: absolute; inset: 0; background: var(--border); border-radius: 999px; transition: background .15s; }
.adm-toggle input:checked + .adm-toggle-track { background: var(--accent); }
.adm-toggle input:disabled + .adm-toggle-track { opacity: .5; }
.adm-toggle-thumb { position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; transition: transform .15s; box-shadow: 0 1px 2px rgba(0,0,0,.25); }
.adm-toggle input:checked + .adm-toggle-track .adm-toggle-thumb { transform: translateX(16px); }
`;

const PAYROLL_STATUS_COLOR = {
  draft:         { color: "#6080aa", bg: "rgba(96,128,170,.1)",  border: "rgba(96,128,170,.25)" },
  calculated:    { color: "#059669", bg: "rgba(16,185,129,.1)",  border: "rgba(16,185,129,.25)" },
  reviewed:      { color: "#7c3aed", bg: "rgba(124,58,237,.1)",  border: "rgba(124,58,237,.25)" },
  approved:      { color: "#0284c7", bg: "rgba(2,132,199,.1)",   border: "rgba(2,132,199,.25)" },
  wps_generated: { color: "#b45309", bg: "rgba(180,83,9,.1)",    border: "rgba(180,83,9,.25)" },
  paid:          { color: "#166534", bg: "rgba(22,101,52,.1)",   border: "rgba(22,101,52,.25)" },
};

// FRD §41 — once a period reaches any of these, entries are locked and
// must not be silently changed. Mirrors LOCKED_STATUSES in routers/payroll.py.
const LOCKED_STATUSES = ["approved", "wps_generated", "paid"];

function Badge({ label, type, colorMap }) {
  const c = colorMap[type] || colorMap.draft || Object.values(colorMap)[0];
  return (
    <span className="adm-badge" style={{ color: c.color, background: c.bg, borderColor: c.border }}>
      <span className="adm-badge-dot" />
      {(label || "").replace(/_/g, " ")}
    </span>
  );
}

function IconArrowLeft(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M11 18l-6-6 6-6" />
    </svg>
  );
}

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const fmt = (n) => {
  const v = Number(n);
  return isNaN(v) ? "0.000" : v.toFixed(3);
};

const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

// Derives the §38 summary totals directly from the entries already on
// screen, instead of a separate network round trip after every edit.
// This is deliberate, not just an optimization: fetching summary
// separately after each PATCH created a real race condition — editing two
// fields in quick succession could fire overlapping GET /summary requests
// whose responses could resolve out of order, letting an older, now-stale
// response land last and overwrite the correct one. Computing it locally
// from `entries` (which is itself always updated synchronously from each
// PATCH's own response) can never be stale relative to what's rendered.
const num = (v) => (v == null ? 0 : Number(v));
const computeSummary = (entries) => entries.reduce((acc, e) => ({
  total_eligible_employees: acc.total_eligible_employees + 1,
  total_basic_salary: acc.total_basic_salary + num(e.prorated_basic),
  total_fixed_other_amount: acc.total_fixed_other_amount + num(e.prorated_other_amount),
  total_additional_amount: acc.total_additional_amount + num(e.additional_amount),
  gross_payroll: acc.gross_payroll + num(e.gross_pay),
  social_statutory_deduction: acc.social_statutory_deduction + num(e.statutory_deduction),
  other_deductions: acc.other_deductions + num(e.other_deduction),
  total_deductions: acc.total_deductions + num(e.total_deduction),
  net_payroll: acc.net_payroll + num(e.net_salary),
}), {
  total_eligible_employees: 0, total_basic_salary: 0, total_fixed_other_amount: 0,
  total_additional_amount: 0, gross_payroll: 0, social_statutory_deduction: 0,
  other_deductions: 0, total_deductions: 0, net_payroll: 0,
});

export default function Payroll() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "H").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const flash = (text) => {
    // Fixed-position toast (.adm-toast) is always visible — no scroll-to-top
    // needed anymore.
    setMsg(text);
    setTimeout(() => setMsg(""), 3000);
  };

  const [periods, setPeriods] = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(true);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  const today = new Date();
  const [newPeriod, setNewPeriod] = useState({
    period_month: today.getMonth() + 1,
    period_year: today.getFullYear(),
    payroll_working_days: "",
  });
  const [creatingPeriod, setCreatingPeriod] = useState(false);

  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const summary = useMemo(() => entries.length > 0 ? computeSummary(entries) : null, [entries]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Workflow (FRD §39-41): validation panel + Review/Approve/Reopen
  const [validation, setValidation] = useState(null);
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [addingReopenReason, setAddingReopenReason] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [markingPaid, setMarkingPaid] = useState(false);

  // WPS Engine (FRD §42-50): bank picker + validation + generate + history
  const [wpsBanks, setWpsBanks] = useState([]);
  const [selectedBankKey, setSelectedBankKey] = useState("");
  const [wpsValidation, setWpsValidation] = useState(null);
  const [wpsWarningsDismissed, setWpsWarningsDismissed] = useState(false);
  const [wpsPreview, setWpsPreview] = useState(null);
  const [showWpsPreview, setShowWpsPreview] = useState(true);
  const [generatingWps, setGeneratingWps] = useState(false);
  const [wpsFiles, setWpsFiles] = useState([]);       // this period's files
  const [wpsHistory, setWpsHistory] = useState([]);   // org-wide history
  const [showWpsHistory, setShowWpsHistory] = useState(false);
  const [addingBank, setAddingBank] = useState(false);
  const [newBankLabel, setNewBankLabel] = useState("");
  const [newBankAbbr, setNewBankAbbr] = useState("");
  const [savingNewBank, setSavingNewBank] = useState(false);

  // Reports (FRD §51-52): Register/Summary exports + Payroll Comparison
  const [showReports, setShowReports] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [compareToPeriodId, setCompareToPeriodId] = useState("");

  const [prorationConfig, setProrationConfig] = useState([]);
  const [showProrationConfig, setShowProrationConfig] = useState(false);
  const [savingProrationFor, setSavingProrationFor] = useState(null);

  const selectedPeriod = periods.find(p => p.id === selectedPeriodId) || null;

  const loadPeriods = async () => {
    setPeriodsLoading(true);
    try {
      const res = await api.get("/payroll/periods");
      setPeriods(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load payroll periods.");
    } finally {
      setPeriodsLoading(false);
    }
  };

  useEffect(() => { loadPeriods(); }, []);

  const createPeriod = async () => {
    setError("");
    setCreatingPeriod(true);
    try {
      const body = {
        period_month: Number(newPeriod.period_month),
        period_year: Number(newPeriod.period_year),
      };
      if (newPeriod.payroll_working_days) body.payroll_working_days = Number(newPeriod.payroll_working_days);
      const res = await api.post("/payroll/periods", body);
      flash("Payroll period created.");
      await loadPeriods();
      await openPeriod(res.data.id);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create payroll period.");
    } finally {
      setCreatingPeriod(false);
    }
  };

  const openPeriod = async (periodId) => {
    setSelectedPeriodId(periodId);
    setError("");
    setValidation(null);
    setWarningsDismissed(false);
    setWpsWarningsDismissed(false);
    setComparison(null);
    setShowReports(false);
    setEntriesLoading(true);
    try {
      let entriesRes = await api.get(`/payroll/periods/${periodId}/entries`);
      // Auto-load eligible employees the first time a period is opened
      // (nothing loaded yet), so HR doesn't have to remember to click
      // "Load Employees" first. The button stays available too, for
      // re-syncing later — e.g. a new hire becoming eligible after this.
      if (entriesRes.data.length === 0) {
        const loadRes = await api.post(`/payroll/periods/${periodId}/load-employees`);
        if (loadRes.data.added > 0) {
          entriesRes = await api.get(`/payroll/periods/${periodId}/entries`);
        }
      }
      setEntries(entriesRes.data);
      // Once a period has actually been calculated, load its validation
      // panel right away so HR can self-check before ever clicking Review.
      const status = periods.find(p => p.id === periodId)?.status;
      if (status && status !== "draft") await loadValidation(periodId);
      if (status === "approved" || status === "wps_generated") {
        if (wpsBanks.length === 0) loadWpsBanks();
        await loadWpsFiles(periodId);
        await loadWpsPreview(periodId);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load payroll entries.");
    } finally {
      setEntriesLoading(false);
    }
  };

  const loadValidation = async (periodId) => {
    try {
      const res = await api.get(`/payroll/periods/${periodId || selectedPeriodId}/validation`);
      setValidation(res.data);
      setWarningsDismissed(false); // fresh data — show warnings again even if a previous set was dismissed
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load payroll validation.");
    }
  };

  const loadEmployeesIntoPeriod = async () => {
    if (!selectedPeriodId) return;
    setError("");
    setLoadingEmployees(true);
    try {
      const res = await api.post(`/payroll/periods/${selectedPeriodId}/load-employees`);
      flash(`${res.data.added} employee(s) loaded into this payroll.`, { scroll: false });
      await openPeriod(selectedPeriodId);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load employees.");
    } finally {
      setLoadingEmployees(false);
    }
  };

  const patchEntryField = async (entryId, field, value) => {
    try {
      // The backend recalculates this one row live and returns its fresh
      // computed figures in the same response — no separate "Calculate"
      // click needed for a single edited row. Merging that response
      // straight into `entries` (rather than a follow-up GET) also means
      // the summary — which is derived from `entries`, see computeSummary
      // above — updates in the exact same state update, with no
      // possibility of a second, slower request landing later and
      // clobbering it with stale numbers.
      const res = await api.patch(`/payroll/entries/${entryId}`, { [field]: value });
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, ...res.data } : e));
      // Mirrors the backend's own rule: a successful recalculation (net_salary
      // now set) means the period counts as "calculated"; failing to
      // recalculate (no resolvable salary structure) downgrades an
      // already-'calculated' period back to 'draft'.
      const recalculated = res.data.net_salary != null;
      setPeriods(prev => prev.map(p => {
        if (p.id !== selectedPeriodId) return p;
        if (recalculated) return { ...p, status: "calculated" };
        return p.status === "calculated" ? { ...p, status: "draft" } : p;
      }));
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save.");
    }
  };

  const calculatePeriod = async () => {
    if (!selectedPeriodId) return;
    setError("");
    setCalculating(true);
    try {
      await api.post(`/payroll/periods/${selectedPeriodId}/calculate`);
      flash("Payroll calculated.", { scroll: false });
      const entriesRes = await api.get(`/payroll/periods/${selectedPeriodId}/entries`);
      setEntries(entriesRes.data);
      setPeriods(prev => prev.map(p => p.id === selectedPeriodId ? { ...p, status: "calculated" } : p));
      await loadValidation(selectedPeriodId);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to calculate payroll.");
    } finally {
      setCalculating(false);
    }
  };

  const reviewPeriod = async () => {
    if (!selectedPeriodId) return;
    setError("");
    setReviewing(true);
    try {
      await api.post(`/payroll/periods/${selectedPeriodId}/review`);
      flash("Payroll reviewed — ready for approval.", { scroll: false });
      setPeriods(prev => prev.map(p => p.id === selectedPeriodId ? { ...p, status: "reviewed" } : p));
      await loadValidation(selectedPeriodId);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to review payroll.");
    } finally {
      setReviewing(false);
    }
  };

  const approvePeriod = async () => {
    if (!selectedPeriodId) return;
    setError("");
    setApproving(true);
    try {
      const res = await api.post(`/payroll/periods/${selectedPeriodId}/approve`);
      flash("Payroll approved and locked.", { scroll: false });
      setPeriods(prev => prev.map(p => p.id === selectedPeriodId ? { ...p, status: "approved" } : p));
      setValidation(v => ({ blocking_errors: [], warnings: res.data.warnings || v?.warnings || [] }));
      if (wpsBanks.length === 0) loadWpsBanks();
      await loadWpsPreview(selectedPeriodId);
    } catch (e) {
      // The backend returns blocking_errors inside the 400 error detail —
      // surface them in the validation panel instead of just a generic
      // error message, so it's clear exactly what needs fixing.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blocking_errors) {
        setValidation(v => ({ ...v, blocking_errors: detail.blocking_errors }));
        setError(detail.message || "Cannot approve — blocking errors must be resolved first.");
      } else {
        setError(detail || "Failed to approve payroll.");
      }
    } finally {
      setApproving(false);
    }
  };

  const markPaidPeriod = async () => {
    if (!selectedPeriodId) return;
    if (!window.confirm("Mark this payroll as paid? This confirms the bank transfer has actually gone through.")) return;
    setError("");
    setMarkingPaid(true);
    try {
      await api.post(`/payroll/periods/${selectedPeriodId}/mark-paid`);
      flash("Payroll marked as paid.", { scroll: false });
      setPeriods(prev => prev.map(p => p.id === selectedPeriodId ? { ...p, status: "paid" } : p));
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to mark payroll as paid.");
    } finally {
      setMarkingPaid(false);
    }
  };

  const reopenPeriod = async () => {
    const reason = reopenReason.trim();
    if (!reason || !selectedPeriodId) return;
    setError("");
    setReopening(true);
    try {
      await api.post(`/payroll/periods/${selectedPeriodId}/reopen`, { reason });
      flash("Payroll reopened — editing is enabled again.", { scroll: false });
      setReopenReason("");
      setAddingReopenReason(false);
      setPeriods(prev => prev.map(p => p.id === selectedPeriodId ? { ...p, status: "calculated" } : p));
      await openPeriod(selectedPeriodId);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to reopen payroll.");
    } finally {
      setReopening(false);
    }
  };

  const loadWpsBanks = async () => {
    try {
      const res = await api.get("/wps/banks");
      setWpsBanks(res.data);
      if (res.data.length > 0 && !selectedBankKey) setSelectedBankKey(res.data[0].key);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load supported banks.");
    }
  };

  const saveNewBank = async () => {
    const label = newBankLabel.trim();
    const abbr = newBankAbbr.trim();
    if (!label || !abbr) return;
    setSavingNewBank(true);
    try {
      const res = await api.post("/wps/banks", { label, wps_abbreviation: abbr });
      setNewBankLabel("");
      setNewBankAbbr("");
      setAddingBank(false);
      await loadWpsBanks();
      setSelectedBankKey(res.data.key);
      flash("Bank added.", { scroll: false });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add bank.");
    } finally {
      setSavingNewBank(false);
    }
  };

  const loadWpsValidation = async (bankKey) => {
    if (!selectedPeriodId) return;
    try {
      const res = await api.get(`/wps/periods/${selectedPeriodId}/validation`, { params: { bank_key: bankKey } });
      setWpsValidation(res.data);
      setWpsWarningsDismissed(false); // fresh data — show warnings again even if a previous set was dismissed
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load WPS validation.");
    }
  };

  // Re-check WPS validation whenever the bank picker changes (a bank
  // mismatch warning depends on which bank is selected).
  useEffect(() => {
    if (selectedBankKey && selectedPeriod && ["approved", "wps_generated"].includes(selectedPeriod.status)) {
      loadWpsValidation(selectedBankKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBankKey]);

  const loadWpsFiles = async (periodId) => {
    try {
      const res = await api.get(`/wps/periods/${periodId || selectedPeriodId}/files`);
      setWpsFiles(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load WPS files.");
    }
  };

  // Human-readable view of the exact same data the CSV generator uses —
  // the real downloadable file deliberately has no descriptive column
  // headers (a real bank WPS system parses it by fixed field position,
  // not by label — adding labels to that file would make it invalid for
  // submission), so this table is how HR actually reviews the numbers.
  const loadWpsPreview = async (periodId) => {
    try {
      const res = await api.get(`/wps/periods/${periodId || selectedPeriodId}/preview`);
      setWpsPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load WPS preview.");
    }
  };

  const generateWps = async () => {
    if (!selectedPeriodId || !selectedBankKey) return;
    setError("");
    setGeneratingWps(true);
    try {
      const res = await api.post(`/wps/periods/${selectedPeriodId}/generate`, null, { params: { bank_key: selectedBankKey } });
      flash(`WPS file generated: ${res.data.file_name}`, { scroll: false });
      setPeriods(prev => prev.map(p => p.id === selectedPeriodId ? { ...p, status: "wps_generated" } : p));
      await loadWpsFiles(selectedPeriodId);
      if (showWpsHistory) await loadWpsHistory();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blocking_errors) {
        setWpsValidation(v => ({ ...v, blocking_errors: detail.blocking_errors }));
        setError(detail.message || "Cannot generate WPS file — blocking errors must be resolved first.");
      } else {
        setError(detail || "Failed to generate WPS file.");
      }
    } finally {
      setGeneratingWps(false);
    }
  };

  const loadWpsHistory = async () => {
    try {
      const res = await api.get("/wps/files");
      setWpsHistory(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load WPS history.");
    }
  };

  const toggleWpsHistory = () => {
    const next = !showWpsHistory;
    setShowWpsHistory(next);
    if (next && wpsHistory.length === 0) loadWpsHistory();
  };

  const loadComparison = async (compareTo) => {
    if (!selectedPeriodId) return;
    setLoadingComparison(true);
    try {
      const res = await api.get(`/reports/periods/${selectedPeriodId}/comparison`,
        compareTo ? { params: { compare_to: compareTo } } : {});
      setComparison(res.data);
      setCompareToPeriodId(res.data.previous ? res.data.previous.period_id : "");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load payroll comparison.");
    } finally {
      setLoadingComparison(false);
    }
  };

  const toggleReports = () => {
    const next = !showReports;
    setShowReports(next);
    if (next && !comparison) loadComparison();
  };

  const downloadBlob = async (endpointUrl, fileName) => {
    try {
      const res = await api.get(endpointUrl, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Could not download WPS file.");
    }
  };

  // The real bank-submission file -- Sohar International's actual WPS
  // template, which already has proper column headers, so this one file is
  // both what gets submitted and what's readable by a human.
  const downloadWpsFile = (fileId, fileName) => downloadBlob(`/wps/files/${fileId}/download`, fileName);

  const loadProrationConfig = async () => {
    try {
      const res = await api.get("/payroll/proration-config");
      setProrationConfig(res.data.components);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load proration settings.");
    }
  };

  const toggleProrationConfig = () => {
    const next = !showProrationConfig;
    setShowProrationConfig(next);
    if (next && prorationConfig.length === 0) loadProrationConfig();
  };

  const setComponentProrate = async (componentType, prorate) => {
    setSavingProrationFor(componentType);
    try {
      await api.put(`/payroll/proration-config/${componentType}`, { prorate });
      setProrationConfig(prev => prev.map(c => c.component_type === componentType ? { ...c, prorate } : c));
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save proration setting.");
    } finally {
      setSavingProrationFor(null);
    }
  };

  // Adding/removing here writes to the SAME shared salary-component-options
  // list the Salary tab's "+ Add new component" flow uses — one registry,
  // reachable from either screen, instead of two lists that could drift
  // apart.
  const [addingProrationComponent, setAddingProrationComponent] = useState(false);
  const [newProrationComponentLabel, setNewProrationComponentLabel] = useState("");
  const [savingNewProrationComponent, setSavingNewProrationComponent] = useState(false);
  const [removingProrationFor, setRemovingProrationFor] = useState(null);

  const saveNewProrationComponent = async () => {
    const label = newProrationComponentLabel.trim();
    if (!label) return;
    setSavingNewProrationComponent(true);
    try {
      await api.post("/employees/salary-component-options", { label });
      setNewProrationComponentLabel("");
      setAddingProrationComponent(false);
      await loadProrationConfig();
      flash("Component added.", { scroll: false });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add component.");
    } finally {
      setSavingNewProrationComponent(false);
    }
  };

  const removeProrationComponent = async (componentType) => {
    setRemovingProrationFor(componentType);
    try {
      await api.delete(`/employees/salary-component-options/${componentType}`);
      setProrationConfig(prev => prev.filter(c => c.component_type !== componentType));
      flash("Component removed.", { scroll: false });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to remove component.");
    } finally {
      setRemovingProrationFor(null);
    }
  };

  // Extracted so it can render both at the top of the period AND next to
  // the Payroll Entries table — HR shouldn't have to scroll back up just
  // to click Review/Approve/Reopen while looking at the entries they're
  // actually reviewing.
  const workflowActions = selectedPeriod && (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
      {(selectedPeriod.status === "draft" || selectedPeriod.status === "calculated") && (
        <>
          <button className="adm-section-btn" disabled={loadingEmployees} onClick={loadEmployeesIntoPeriod}>
            {loadingEmployees ? "Loading…" : "Load Employees"}
          </button>
          <button className="adm-submit" disabled={calculating || entries.length === 0} onClick={calculatePeriod}>
            {calculating ? "Calculating…" : "Calculate"}
          </button>
        </>
      )}
      {selectedPeriod.status === "calculated" && (
        <button className="adm-submit" disabled={reviewing || entries.length === 0} onClick={reviewPeriod}>
          {reviewing ? "Reviewing…" : "Review"}
        </button>
      )}
      {selectedPeriod.status === "reviewed" && (
        <button className="adm-submit" disabled={approving} onClick={approvePeriod}>
          {approving ? "Approving…" : "Approve"}
        </button>
      )}
      {selectedPeriod.status === "wps_generated" && (
        <button className="adm-submit" disabled={markingPaid} onClick={markPaidPeriod}>
          {markingPaid ? "Marking Paid…" : "Mark Paid"}
        </button>
      )}
      {LOCKED_STATUSES.includes(selectedPeriod.status) && (
        addingReopenReason ? (
          <div style={{ display: "flex", gap: 6 }}>
            <input className="adm-input adm-input-sm" style={{ width: 220 }} autoFocus
                   disabled={reopening} value={reopenReason}
                   onChange={e => setReopenReason(e.target.value)}
                   onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); reopenPeriod(); } if (e.key === "Escape") setAddingReopenReason(false); }}
                   placeholder="Reason for reopening…" />
            <button className="adm-action-btn" disabled={reopening || !reopenReason.trim()} onClick={reopenPeriod}>
              {reopening ? "Reopening…" : "Confirm Reopen"}
            </button>
            <button className="adm-action-btn" disabled={reopening} onClick={() => { setAddingReopenReason(false); setReopenReason(""); }}>Cancel</button>
          </div>
        ) : (
          <button className="adm-action-btn" onClick={() => setAddingReopenReason(true)}>Reopen</button>
        )
      )}
    </div>
  );

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />
      <style>{CSS}</style>
      <header className="adm-topbar">
        <button className="adm-back" onClick={() => navigate("/")} title="Back"><IconArrowLeft /></button>
        <div className="adm-brand">
          <div className="adm-brand-mark">💰</div>
          <span className="adm-brand-label">HRMS & Payroll — Payroll</span>
        </div>
        <div style={{ marginRight: 8, fontSize: 12, color: "#6080aa" }}>{user?.org_name}</div>
        <div className="adm-avatar" title={user?.name}>{initials}</div>
      </header>

      <div className="adm-tabs">
        <button className="adm-tab" onClick={() => navigate("/hrms/dashboard")}>Dashboard</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/employees")}>Employees</button>
        <button className="adm-tab active">Payroll</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/expiry")}>Document Expiry</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/data-quality")}>Data Quality</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/company-documents")}>Company Documents</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/assistant")}>AI Assistant</button>
      </div>

      <div className="adm-main">
        {error && <div className="adm-error">{error}</div>}
        {msg && <div className="adm-toast"><span className="adm-toast-dot" />{msg}</div>}

        {!selectedPeriodId && (
          <>
            <div className="adm-card">
              <div className="adm-card-title">Create Payroll Period</div>
              <div className="adm-form-grid">
                <div className="adm-field">
                  <label className="adm-label">Month</label>
                  <select className="adm-select" value={newPeriod.period_month}
                          onChange={e => setNewPeriod(p => ({ ...p, period_month: e.target.value }))}>
                    {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div className="adm-field">
                  <label className="adm-label">Year</label>
                  <input className="adm-input" type="number" value={newPeriod.period_year}
                         onChange={e => setNewPeriod(p => ({ ...p, period_year: e.target.value }))} />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Payroll Working Days</label>
                  <input className="adm-input" type="number" placeholder="e.g. 30" value={newPeriod.payroll_working_days}
                         onChange={e => setNewPeriod(p => ({ ...p, payroll_working_days: e.target.value }))} />
                </div>
              </div>
              <button className="adm-submit" style={{ marginTop: 16 }} disabled={creatingPeriod} onClick={createPeriod}>
                {creatingPeriod ? "Creating…" : "Create Payroll"}
              </button>
            </div>

            <div className="adm-card">
              <div className="adm-card-title">Payroll Periods</div>
              {periodsLoading ? (
                <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
              ) : periods.length === 0 ? (
                <div className="adm-empty">No payroll periods yet — create one above.</div>
              ) : (
                <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead><tr><th>Period</th><th>Working Days</th><th>Status</th></tr></thead>
                    <tbody>
                      {periods.map(p => (
                        <tr key={p.id} className="clickable" onClick={() => openPeriod(p.id)}>
                          <td>{MONTH_NAMES[p.period_month - 1]} {p.period_year}</td>
                          <td>{p.payroll_working_days}</td>
                          <td><Badge label={p.status} type={p.status} colorMap={PAYROLL_STATUS_COLOR} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="adm-card">
              <div className="adm-card-title" style={{ cursor: "pointer" }} onClick={toggleWpsHistory}>
                WPS Files {showWpsHistory ? "▲" : "▼"}
              </div>
              {showWpsHistory && (
                wpsHistory.length === 0 ? (
                  <div className="adm-empty">No WPS files generated yet.</div>
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>Period</th><th>Bank</th><th>File Name</th><th>Employees</th>
                          <th>Total Salary</th><th>Generated</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {wpsHistory.map(f => (
                          <tr key={f.id}>
                            <td>{MONTH_NAMES[f.period_month - 1]} {f.period_year}</td>
                            <td>{f.bank_name}</td>
                            <td>{f.file_name}</td>
                            <td>{f.employee_count}</td>
                            <td>{fmt(f.total_salary)}</td>
                            <td>{new Date(f.generated_at).toLocaleString()}</td>
                            <td>
                              <button className="adm-action-btn" onClick={() => downloadWpsFile(f.id, f.file_name)}>Download</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>

            <div className="adm-card">
              <div className="adm-card-title" style={{ cursor: "pointer" }} onClick={toggleProrationConfig}>
                Proration Settings {showProrationConfig ? "▲" : "▼"}
              </div>
              {showProrationConfig && (
                <>
                  <div className="adm-card-desc">
                    Controls whether each salary component shrinks for a partial month. <strong>On</strong> = paid
                    proportionally to days worked (e.g. 27 of 30 working days pays 27/30ths). <strong>Off</strong> = always
                    paid in full, regardless of days worked.
                  </div>
                  {prorationConfig.length === 0 ? (
                    <div className="adm-empty">No salary components configured yet.</div>
                  ) : (
                    <div className="adm-table-wrap">
                      <table className="adm-table">
                        <thead><tr><th>Component</th><th>Prorate by Working Days</th><th></th></tr></thead>
                        <tbody>
                          {prorationConfig.map(c => (
                            <tr key={c.component_type}>
                              <td>{titleCase(c.component_type)}</td>
                              <td>
                                <label className="adm-toggle">
                                  <input type="checkbox" checked={c.prorate} disabled={savingProrationFor === c.component_type}
                                         onChange={e => setComponentProrate(c.component_type, e.target.checked)} />
                                  <span className="adm-toggle-track"><span className="adm-toggle-thumb" /></span>
                                </label>
                              </td>
                              <td>
                                {!c.is_builtin && (
                                  <button className="adm-action-btn" disabled={removingProrationFor === c.component_type}
                                          onClick={() => removeProrationComponent(c.component_type)}>
                                    {removingProrationFor === c.component_type ? "Removing…" : "Remove"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div style={{ marginTop: 14 }}>
                    {addingProrationComponent ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <input className="adm-input adm-input-sm" style={{ width: 180 }} autoFocus
                               disabled={savingNewProrationComponent} value={newProrationComponentLabel}
                               onChange={e => setNewProrationComponentLabel(e.target.value)}
                               onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); saveNewProrationComponent(); } if (e.key === "Escape") setAddingProrationComponent(false); }}
                               placeholder="e.g. Telephone Allowance" />
                        <button className="adm-action-btn" disabled={savingNewProrationComponent} onClick={saveNewProrationComponent}>
                          {savingNewProrationComponent ? "Saving…" : "Save"}
                        </button>
                        <button className="adm-action-btn" disabled={savingNewProrationComponent} onClick={() => setAddingProrationComponent(false)}>Cancel</button>
                      </div>
                    ) : (
                      <button className="adm-action-btn" onClick={() => setAddingProrationComponent(true)}>+ Add new component</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {selectedPeriodId && selectedPeriod && (
          <>
            <div className="adm-card">
              <div className="adm-card-title">
                <span>
                  {MONTH_NAMES[selectedPeriod.period_month - 1]} {selectedPeriod.period_year}
                  {" "}<Badge label={selectedPeriod.status} type={selectedPeriod.status} colorMap={PAYROLL_STATUS_COLOR} />
                </span>
                <button className="adm-back-link" onClick={() => { setSelectedPeriodId(null); setEntries([]); }}>
                  ← Back to Periods
                </button>
              </div>
              {workflowActions}
            </div>

            {validation && (validation.blocking_errors.length > 0 || validation.warnings.length > 0) && (
              <div className="adm-card">
                <div className="adm-card-title">Validation</div>
                {validation.blocking_errors.length > 0 && (
                  <div className="adm-error">
                    <strong>Blocking — must be resolved before Approve:</strong>
                    <ul style={{ margin: "6px 0 0 18px" }}>
                      {validation.blocking_errors.map((v, i) => (
                        <li key={i}>{v.employee_code} — {v.full_name}: {v.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {validation.warnings.length > 0 && !warningsDismissed && (
                  <div className="adm-warning">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <strong>Warnings (informational, won't block Approve):</strong>
                      <button type="button" onClick={() => setWarningsDismissed(true)}
                              style={{ background: "none", border: "none", color: "#b45309", fontSize: 16, lineHeight: 1, cursor: "pointer", padding: 0 }}
                              title="Dismiss">✕</button>
                    </div>
                    <ul style={{ margin: "6px 0 0 18px" }}>
                      {validation.warnings.map((v, i) => (
                        <li key={i}>{v.employee_code} — {v.full_name}: {v.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {["approved", "wps_generated", "paid"].includes(selectedPeriod.status) && (
              <div className="adm-card">
                <div className="adm-card-title">WPS (Wage Protection System)</div>
                <div className="adm-card-desc">
                  {selectedPeriod.status === "paid"
                    ? "This period is already paid — the file already submitted to the bank is below."
                    : "Generates the Oman WPS salary file for this period, ready for bank submission."}
                </div>

                {wpsFiles.length > 0 && (
                  <div className="adm-wps-existing">
                    <div className="adm-wps-existing-text">
                      <strong>✓ Already generated</strong> — {wpsFiles[0].file_name} ({wpsFiles[0].bank_name}, v{wpsFiles[0].file_version}),{" "}
                      {new Date(wpsFiles[0].generated_at).toLocaleString()}
                      {wpsFiles.length > 1 && <> · {wpsFiles.length} files total for this period, see history below</>}
                    </div>
                    <button className="adm-submit" style={{ marginTop: 0 }}
                            onClick={() => downloadWpsFile(wpsFiles[0].id, wpsFiles[0].file_name)}>
                      Download
                    </button>
                  </div>
                )}

                {selectedPeriod.status !== "paid" && (
                <>
                <div className="adm-form-grid" style={{ marginBottom: 14 }}>
                  <div className="adm-field">
                    <label className="adm-label">Bank</label>
                    {addingBank ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <input className="adm-input" style={{ maxWidth: 160 }} autoFocus
                               disabled={savingNewBank} value={newBankLabel}
                               onChange={e => setNewBankLabel(e.target.value)}
                               onKeyDown={e => { if (e.key === "Escape") setAddingBank(false); }}
                               placeholder="Bank name" />
                        <input className="adm-input" style={{ maxWidth: 100 }}
                               disabled={savingNewBank} value={newBankAbbr}
                               onChange={e => setNewBankAbbr(e.target.value.toUpperCase())}
                               onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); saveNewBank(); } if (e.key === "Escape") setAddingBank(false); }}
                               placeholder="WPS abbr." />
                        <button className="adm-action-btn" disabled={savingNewBank || !newBankLabel.trim() || !newBankAbbr.trim()} onClick={saveNewBank}>
                          {savingNewBank ? "Saving…" : "Save"}
                        </button>
                        <button className="adm-action-btn" disabled={savingNewBank} onClick={() => setAddingBank(false)}>Cancel</button>
                      </div>
                    ) : (
                      <select className="adm-select" value={selectedBankKey}
                              onChange={e => {
                                if (e.target.value === "__add_new__") { setAddingBank(true); return; }
                                setSelectedBankKey(e.target.value);
                              }}>
                        {wpsBanks.length === 0 && <option value="">Loading…</option>}
                        {wpsBanks.map(b => <option key={b.key} value={b.key}>{b.name}</option>)}
                        <option value="__add_new__">+ Add new bank…</option>
                      </select>
                    )}
                  </div>
                </div>

                {wpsPreview && wpsPreview.employees.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div className="adm-label" style={{ marginBottom: 8, cursor: "pointer" }}
                         onClick={() => setShowWpsPreview(v => !v)}>
                      Preview — what will be included {showWpsPreview ? "▲" : "▼"} ({wpsPreview.employees.length} employees)
                    </div>
                    {showWpsPreview && (
                      <div className="adm-table-wrap">
                        <table className="adm-table">
                          <thead>
                            <tr>
                              <th>Employee</th><th>ID Type</th><th>Employee ID</th><th>BIC Code</th><th>Account</th>
                              <th>Working Days</th><th>Basic Salary</th><th>Extra Hours</th><th>Extra Income</th>
                              <th>Deductions</th><th>Social Security</th><th>Net Salary</th>
                            </tr>
                          </thead>
                          <tbody>
                            {wpsPreview.employees.map(emp => (
                              <tr key={emp.employee_id}>
                                <td>{emp.employee_code} — {emp.employee_name}</td>
                                <td>{emp.id_type || "—"}</td>
                                <td>{emp.id_number || "—"}</td>
                                <td>{emp.bic_code || "—"}</td>
                                <td>{emp.account_number || "—"}</td>
                                <td>{emp.working_days ?? "—"}</td>
                                <td>{fmt(emp.basic_salary)}</td>
                                <td>{emp.extra_hours ?? 0}</td>
                                <td>{fmt(emp.extra_income)}</td>
                                <td>{fmt(emp.deductions)}</td>
                                <td>{fmt(emp.statutory_deduction)}</td>
                                <td>{fmt(emp.net_salary)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan={6}><strong>Total ({wpsPreview.totals.employee_count} employees)</strong></td>
                              <td><strong>{fmt(wpsPreview.totals.total_basic_salary)}</strong></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td><strong>{fmt(wpsPreview.totals.total_net_salary)}</strong></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {wpsValidation && (wpsValidation.blocking_errors.length > 0 || wpsValidation.warnings.length > 0) && (
                  <>
                    {wpsValidation.blocking_errors.length > 0 && (
                      <div className="adm-error">
                        <strong>Blocking — must be resolved before generating:</strong>
                        <ul style={{ margin: "6px 0 0 18px" }}>
                          {wpsValidation.blocking_errors.map((v, i) => (
                            <li key={i}>{v.employee_code ? `${v.employee_code} — ${v.full_name}: ` : ""}{v.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {wpsValidation.warnings.length > 0 && !wpsWarningsDismissed && (
                      <div className="adm-warning">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <strong>Warnings (informational, won't block generation):</strong>
                          <button type="button" onClick={() => setWpsWarningsDismissed(true)}
                                  style={{ background: "none", border: "none", color: "#b45309", fontSize: 16, lineHeight: 1, cursor: "pointer", padding: 0 }}
                                  title="Dismiss">✕</button>
                        </div>
                        <ul style={{ margin: "6px 0 0 18px" }}>
                          {wpsValidation.warnings.map((v, i) => (
                            <li key={i}>{v.employee_code ? `${v.employee_code} — ${v.full_name}: ` : ""}{v.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}

                <button className={wpsFiles.length > 0 ? "adm-btn-secondary" : "adm-submit"}
                        disabled={generatingWps || !selectedBankKey || (wpsValidation?.blocking_errors?.length > 0)}
                        onClick={generateWps}>
                  {generatingWps ? "Generating…" : wpsFiles.length > 0 ? "Generate Another Version" : "Generate WPS CSV"}
                </button>
                </>
                )}

                {wpsFiles.length > 0 && (
                  <div className="adm-table-wrap" style={{ marginTop: 16 }}>
                    <table className="adm-table">
                      <thead>
                        <tr><th>Bank</th><th>File Name</th><th>Version</th><th>Generated</th><th></th></tr>
                      </thead>
                      <tbody>
                        {wpsFiles.map(f => (
                          <tr key={f.id}>
                            <td>{f.bank_name}</td>
                            <td>{f.file_name}</td>
                            <td>v{f.file_version}</td>
                            <td>{new Date(f.generated_at).toLocaleString()}</td>
                            <td>
                              <button className="adm-action-btn" onClick={() => downloadWpsFile(f.id, f.file_name)}>Download</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="adm-card">
              <div className="adm-card-title" style={{ cursor: "pointer" }} onClick={toggleReports}>
                Reports {showReports ? "▲" : "▼"}
              </div>
              {showReports && (
                <>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                    <button className="adm-action-btn" onClick={() => downloadBlob(`/reports/periods/${selectedPeriodId}/register.xlsx`, `Payroll_Register_${MONTH_NAMES[selectedPeriod.period_month - 1]}_${selectedPeriod.period_year}.xlsx`)}>
                      Download Register (Excel)
                    </button>
                    <button className="adm-action-btn" onClick={() => downloadBlob(`/reports/periods/${selectedPeriodId}/register.pdf`, `Payroll_Register_${MONTH_NAMES[selectedPeriod.period_month - 1]}_${selectedPeriod.period_year}.pdf`)}>
                      Download Register (PDF)
                    </button>
                    <button className="adm-action-btn" onClick={() => downloadBlob(`/reports/periods/${selectedPeriodId}/summary.xlsx`, `Payroll_Summary_${MONTH_NAMES[selectedPeriod.period_month - 1]}_${selectedPeriod.period_year}.xlsx`)}>
                      Download Summary (Excel)
                    </button>
                    <button className="adm-action-btn" onClick={() => downloadBlob(`/reports/periods/${selectedPeriodId}/summary.pdf`, `Payroll_Summary_${MONTH_NAMES[selectedPeriod.period_month - 1]}_${selectedPeriod.period_year}.pdf`)}>
                      Download Summary (PDF)
                    </button>
                  </div>

                  <div className="adm-label" style={{ marginBottom: 8 }}>Payroll Comparison</div>
                  {loadingComparison ? (
                    <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
                  ) : !comparison ? (
                    <div className="adm-empty">Could not load comparison.</div>
                  ) : !comparison.previous ? (
                    <div className="adm-empty">No earlier payroll period exists for this organisation yet — nothing to compare against.</div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                        <span style={{ fontSize: 12, color: "#6080aa" }}>Comparing against:</span>
                        <select className="adm-select" value={compareToPeriodId}
                                onChange={e => { setCompareToPeriodId(e.target.value); loadComparison(e.target.value); }}>
                          {periods.filter(p => p.id !== selectedPeriodId).map(p => (
                            <option key={p.id} value={p.id}>{MONTH_NAMES[p.period_month - 1]} {p.period_year}</option>
                          ))}
                        </select>
                      </div>
                      <div className="adm-table-wrap">
                        <table className="adm-table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>{MONTH_NAMES[comparison.previous.period_month - 1]} {comparison.previous.period_year}</th>
                              <th>{MONTH_NAMES[comparison.current.period_month - 1]} {comparison.current.period_year}</th>
                              <th>Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Employees</td>
                              <td>{comparison.previous.summary.total_eligible_employees}</td>
                              <td>{comparison.current.summary.total_eligible_employees}</td>
                              <td>{comparison.deltas.employee_count_delta > 0 ? "+" : ""}{comparison.deltas.employee_count_delta}</td>
                            </tr>
                            <tr>
                              <td>Gross Payroll</td>
                              <td>{fmt(comparison.previous.summary.gross_payroll)}</td>
                              <td>{fmt(comparison.current.summary.gross_payroll)}</td>
                              <td>{comparison.deltas.gross_payroll_delta > 0 ? "+" : ""}{fmt(comparison.deltas.gross_payroll_delta)}
                                {comparison.deltas.gross_payroll_pct != null && ` (${comparison.deltas.gross_payroll_pct > 0 ? "+" : ""}${comparison.deltas.gross_payroll_pct}%)`}</td>
                            </tr>
                            <tr>
                              <td>Total Deductions</td>
                              <td>{fmt(comparison.previous.summary.total_deductions)}</td>
                              <td>{fmt(comparison.current.summary.total_deductions)}</td>
                              <td>{comparison.deltas.total_deductions_delta > 0 ? "+" : ""}{fmt(comparison.deltas.total_deductions_delta)}
                                {comparison.deltas.total_deductions_pct != null && ` (${comparison.deltas.total_deductions_pct > 0 ? "+" : ""}${comparison.deltas.total_deductions_pct}%)`}</td>
                            </tr>
                            <tr>
                              <td>Net Payroll</td>
                              <td>{fmt(comparison.previous.summary.net_payroll)}</td>
                              <td>{fmt(comparison.current.summary.net_payroll)}</td>
                              <td>{comparison.deltas.net_payroll_delta > 0 ? "+" : ""}{fmt(comparison.deltas.net_payroll_delta)}
                                {comparison.deltas.net_payroll_pct != null && ` (${comparison.deltas.net_payroll_pct > 0 ? "+" : ""}${comparison.deltas.net_payroll_pct}%)`}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {summary && (
              <div className="adm-card">
                <div className="adm-card-title">Payroll Summary</div>
                <div className="adm-summary-grid">
                  <div className="adm-summary-stat"><div className="v">{summary.total_eligible_employees}</div><div className="l">Employees</div></div>
                  <div className="adm-summary-stat"><div className="v">{fmt(summary.total_basic_salary)}</div><div className="l">Total Basic Salary</div></div>
                  <div className="adm-summary-stat"><div className="v">{fmt(summary.total_fixed_other_amount)}</div><div className="l">Total Fixed Other Amount</div></div>
                  <div className="adm-summary-stat"><div className="v">{fmt(summary.total_additional_amount)}</div><div className="l">Total Additional Amount</div></div>
                  <div className="adm-summary-stat"><div className="v">{fmt(summary.gross_payroll)}</div><div className="l">Gross Payroll</div></div>
                  <div className="adm-summary-stat"><div className="v">{fmt(summary.social_statutory_deduction)}</div><div className="l">Statutory Deduction</div></div>
                  <div className="adm-summary-stat"><div className="v">{fmt(summary.other_deductions)}</div><div className="l">Other Deductions</div></div>
                  <div className="adm-summary-stat"><div className="v">{fmt(summary.total_deductions)}</div><div className="l">Total Deductions</div></div>
                  <div className="adm-summary-stat"><div className="v">{fmt(summary.net_payroll)}</div><div className="l">Net Payroll</div></div>
                </div>
              </div>
            )}

            <div className="adm-card">
              <div className="adm-card-title">
                <span>Payroll Entries ({entries.length})</span>
                {workflowActions}
              </div>
              {LOCKED_STATUSES.includes(selectedPeriod.status) && (
                <div className="adm-card-desc" style={{ marginTop: 0 }}>
                  🔒 This payroll is approved and locked — figures are read-only. Use Reopen above to make changes.
                </div>
              )}
              {entriesLoading ? (
                <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
              ) : entries.length === 0 ? (
                <div className="adm-empty">No employees loaded yet — click "Load Employees" above.</div>
              ) : (
                <div className="adm-table-wrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>Employee</th><th>Basic</th><th>Other Fixed</th><th>Working Days</th>
                        <th>Extra Hours</th><th>Additional Amount</th><th>Other Deduction</th><th>Deduction Reason</th>
                        <th>Remarks</th><th>Net Salary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map(e => {
                        const locked = LOCKED_STATUSES.includes(selectedPeriod.status);
                        return (
                        <tr key={e.id}>
                          <td>{e.employee_code} — {e.full_name}</td>
                          <td>{e.basic_salary != null ? fmt(e.basic_salary) : "—"}</td>
                          <td>{e.fixed_other_amount != null ? fmt(e.fixed_other_amount) : "—"}</td>
                          <td>
                            <input key={`${e.id}-wd-${e.working_days}`} className="adm-input adm-input-sm" type="number" step="0.01" defaultValue={e.working_days} disabled={locked}
                                   onBlur={ev => { const raw = ev.target.value.trim(); const v = raw === "" ? 0 : parseFloat(raw); if (!isNaN(v) && v !== parseFloat(e.working_days || 0)) patchEntryField(e.id, "working_days", v); }} />
                          </td>
                          <td>
                            <input key={`${e.id}-eh-${e.extra_hours}`} className="adm-input adm-input-sm" type="number" step="0.5" defaultValue={e.extra_hours} disabled={locked}
                                   onBlur={ev => { const raw = ev.target.value.trim(); const v = raw === "" ? 0 : parseFloat(raw); if (!isNaN(v) && v !== parseFloat(e.extra_hours || 0)) patchEntryField(e.id, "extra_hours", v); }} />
                          </td>
                          <td>
                            <input key={`${e.id}-aa-${e.additional_amount}`} className="adm-input adm-input-sm" type="number" step="0.001" defaultValue={e.additional_amount} disabled={locked}
                                   onBlur={ev => { const raw = ev.target.value.trim(); const v = raw === "" ? 0 : parseFloat(raw); if (!isNaN(v) && v !== parseFloat(e.additional_amount || 0)) patchEntryField(e.id, "additional_amount", v); }} />
                          </td>
                          <td>
                            <input key={`${e.id}-od-${e.other_deduction}`} className="adm-input adm-input-sm" type="number" step="0.001" defaultValue={e.other_deduction} disabled={locked}
                                   onBlur={ev => { const raw = ev.target.value.trim(); const v = raw === "" ? 0 : parseFloat(raw); if (!isNaN(v) && v !== parseFloat(e.other_deduction || 0)) patchEntryField(e.id, "other_deduction", v); }} />
                          </td>
                          <td>
                            <input key={`${e.id}-dr-${e.deduction_reason}`} className="adm-input adm-input-sm" style={{ width: 120 }} defaultValue={e.deduction_reason || ""} disabled={locked}
                                   onBlur={ev => { if (ev.target.value !== (e.deduction_reason || "")) patchEntryField(e.id, "deduction_reason", ev.target.value); }} />
                          </td>
                          <td>
                            <input key={`${e.id}-rm-${e.remarks}`} className="adm-input adm-input-sm" style={{ width: 120 }} defaultValue={e.remarks || ""} disabled={locked}
                                   onBlur={ev => { if (ev.target.value !== (e.remarks || "")) patchEntryField(e.id, "remarks", ev.target.value); }} />
                          </td>
                          <td>{e.net_salary != null ? fmt(e.net_salary) : "—"}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
